import { Clock3, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BankReconciliationRunListItem } from '../../api/client';

interface Props {
  runs: BankReconciliationRunListItem[];
  activeRunId?: string;
  loading: boolean;
  onSelect: (runId: string) => void;
  disabled?: boolean;
}

export default function RunHistory({ runs, activeRunId, loading, onSelect, disabled }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">{t('reconciliation.history.title')}</h2>
      </div>
      <div className="max-h-80 overflow-y-auto p-2">
        {loading ? <p className="p-3 text-sm text-gray-500">{t('common.loading')}</p> : null}
        {!loading && runs.length === 0 ? (
          <p className="p-3 text-sm text-gray-500">{t('reconciliation.history.empty')}</p>
        ) : null}
        {runs.map((run) => (
          <button
            type="button"
            key={run.run_id}
            disabled={disabled}
            onClick={() => onSelect(run.run_id)}
            className={`mb-1 w-full rounded-lg p-3 text-start transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              activeRunId === run.run_id ? 'bg-primary-50 text-primary-900' : 'hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate text-sm font-medium">{run.original_filename}</span>
            </span>
            <span className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(run.uploaded_at))}
              </span>
              <span>{run.summary.suggestion_count} {t('reconciliation.history.matches')}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
